import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Moon, Sun } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function UserPreferences() {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/preferences`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updates) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${API_BASE}/dialer/preferences`,
        { ...preferences, ...updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreferences(response.data);
      
      // Apply theme immediately
      if (updates.theme) {
        document.documentElement.classList.toggle('dark', updates.theme === 'dark');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      alert('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !preferences) {
    return <div className="text-center py-4">Loading preferences...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <h3 className="text-xl font-semibold flex items-center gap-2">
        <Settings className="w-6 h-6" />
        User Preferences
      </h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Default Dialing Mode</label>
          <select
            value={preferences.default_dialing_mode}
            onChange={(e) => updatePreferences({ default_dialing_mode: e.target.value })}
            className="w-full border rounded px-3 py-2"
          >
            <option value="manual">Manual</option>
            <option value="preview">Preview</option>
            <option value="predictive">Predictive</option>
          </select>
        </div>

        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={preferences.auto_advance}
              onChange={(e) => updatePreferences({ auto_advance: e.target.checked })}
              className="rounded"
            />
            <span>Auto-advance to next contact</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Theme</label>
          <div className="flex gap-2">
            <button
              onClick={() => updatePreferences({ theme: 'light' })}
              className={`flex-1 px-4 py-2 rounded flex items-center justify-center gap-2 ${
                preferences.theme === 'light' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}
            >
              <Sun className="w-4 h-4" />
              Light
            </button>
            <button
              onClick={() => updatePreferences({ theme: 'dark' })}
              className={`flex-1 px-4 py-2 rounded flex items-center justify-center gap-2 ${
                preferences.theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}
            >
              <Moon className="w-4 h-4" />
              Dark
            </button>
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={preferences.notifications_enabled}
              onChange={(e) => updatePreferences({ notifications_enabled: e.target.checked })}
              className="rounded"
            />
            <span>Enable notifications</span>
          </label>
        </div>
      </div>

      {saving && (
        <p className="text-sm text-gray-500">Saving preferences...</p>
      )}
    </div>
  );
}


